import { useCallback, useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import GetData from "../../services/getData";
import { buildPdfFromLines } from "../../services/pdfDocument";
import { renderNodeToPdf } from "../../services/printToPdf";
import logPatientChange from "../../services/patientLogs";
import { buildPatientLogText } from "../../services/patientLogMessages";
import Toast from "../layout/toast/Toast";
import Button from "./Button";
import "./sendEmailModal.css";

/**
 * Универсальное окно «Отправить на e-mail».
 *
 * Используется одинаково для УЗИ, анкеты и плана лечения:
 *   • PDF собирается на клиенте (см. services/pdfDocument.js) из строк,
 *     которые отдаёт `getLines()` — функция страницы-владельца;
 *   • врач правит сопроводительный текст (по Enter — перенос строки,
 *     отправка ТОЛЬКО по кнопке);
 *   • кнопка «Отправить» активна лишь когда настроен SMTP и у пациента
 *     есть e-mail (проверяется действием patient/send_email: info).
 *
 * Режим «ссылка» (проп getLink): файл не формируется, пациент получает письмо
 * с кнопкой-ссылкой. Так отправляется анкета — её заполняют онлайн.
 */
export default function SendEmailModal({
  show,
  onClose,
  idPatient,
  itemType = "document",
  itemId = 0,
  documentTitle = "Документ",
  subject,
  getLines,
  getRoot,
  getLink,
  onSent,
}) {
  const linkMode = typeof getLink === "function";
  const [info, setInfo] = useState({ loading: true, can_send: false });
  const [subjectText, setSubjectText] = useState(subject || documentTitle || "");
  const [text, setText] = useState("");
  const [pdf, setPdf] = useState(null);
  const [pdfError, setPdfError] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [linkError, setLinkError] = useState("");
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState({});
  const textRef = useRef(null);
  const subjectRef = useRef(null);

  const showToast = useCallback((title, status = "success") => {
    setToast({ id: Date.now(), title, status });
  }, []);

  const fileName = `${documentTitle.replace(/[\\/:*?"<>|]+/g, " ").trim() || "Документ"}.pdf`;

  // Загрузка настроек почты и подготовка вложения.
  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;

    setInfo({ loading: true, can_send: false });
    setPdf(null);
    setPdfError("");
    setLinkUrl("");
    setLinkError("");
    setText("");
    setSubjectText(subject || documentTitle || "");
    setToast({});

    const load = async () => {
      let payload = {};
      try {
        payload =
          (await GetData({
            module: "patient",
            block: "send_email",
            action: "info",
            id_patient: idPatient,
          })) || {};
      } catch (err) {
        console.error("Не удалось получить настройки отправки:", err);
      }
      if (cancelled) return;

      const patientName = payload.patient_name || "";
      setInfo({
        loading: false,
        can_send: Boolean(payload.can_send),
        smtp_configured: Boolean(payload.smtp_configured),
        patient_email: payload.patient_email || "",
        patient_name: patientName,
      });
      setText(
        patientName ? `Здравствуйте, ${patientName}!\n\n` : "Здравствуйте!\n\n"
      );

      try {
        // getRoot — полноценная печатная форма: рендерим её в canvas-PDF
        // со всеми стилями (так выглядят документы, уходящие на печать).
        // getLines — упрощённый текстовый вариант.
        if (typeof getRoot === "function") {
          const node = await Promise.resolve(getRoot());
          if (!node) throw new Error("Печатная форма не найдена");
          const blob = await renderNodeToPdf(node, { title: documentTitle });
          if (!cancelled) setPdf(blob);
          return;
        }
        // getLines может быть асинхронным: печатная форма (план лечения)
        // захватывается из отдельного маршрута печати через iframe.
        const lines = await Promise.resolve(
          typeof getLines === "function" ? getLines() : []
        );
        const blob = await buildPdfFromLines(lines || [], { title: documentTitle });
        if (!cancelled) setPdf(blob);
      } catch (err) {
        console.error("Не удалось сформировать PDF:", err);
        if (!cancelled) setPdfError("Не удалось сформировать PDF-документ");
      }
    };

    // Режим ссылки: файл не формируем, а получаем адрес онлайн-формы.
    const loadLink = async () => {
      try {
        const url = await getLink();
        if (cancelled) return;
        if (url) {
          setLinkUrl(url);
        } else {
          setLinkError("Не удалось создать ссылку");
        }
      } catch (err) {
        console.error("Не удалось создать ссылку:", err);
        if (!cancelled) {
          setLinkError(err?.message || "Не удалось создать ссылку");
        }
      }
    };

    if (linkMode) loadLink();
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, idPatient, itemType, itemId]);

  useEffect(() => {
    if (show && subjectRef.current) {
      // Тема — первое поле формы, ставим курсор туда; текст уже предзаполнен.
      subjectRef.current.focus();
      subjectRef.current.select();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, info.loading]);

  const readyContent = linkMode
    ? Boolean(linkUrl) && !linkError
    : Boolean(pdf) && !pdfError;

  const canSend =
    !sending &&
    !info.loading &&
    info.can_send &&
    readyContent &&
    Boolean(String(subjectText || "").trim());

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      // В режиме ссылки файл не отправляем — только адрес онлайн-формы.
      const file = linkMode
        ? undefined
        : new File([pdf], fileName, { type: "application/pdf" });
      const response = await GetData(
        {
          module: "patient",
          block: "send_email",
          action: "send",
          id_patient: idPatient,
          item_type: itemType,
          item_id: itemId,
          subject: String(subjectText || "").trim() || documentTitle,
          body: text,
          ...(linkMode ? { link: linkUrl } : {}),
        },
        file
      );

      if (!response || response?.success === false) {
        showToast(
          response?.message || "Не удалось отправить письмо",
          "error"
        );
        return;
      }

      showToast(response?.message || "Письмо отправлено", "success");

      // История пациента: фиксируем факт отправки письма.
      try {
        await logPatientChange({
          idPatient,
          idItem: itemId,
          text: buildPatientLogText("patient_email_sent", {
            details: `${documentTitle} → ${response?.to || info.patient_email || ""}`,
          }),
        });
      } catch (err) {
        console.error("Не удалось записать лог пациента:", err);
      }

      if (typeof onSent === "function") {
        onSent(response);
      }
      setTimeout(() => {
        if (typeof onClose === "function") onClose();
      }, 600);
    } catch (err) {
      console.error("Ошибка отправки письма:", err);
      showToast("Не удалось отправить письмо", "error");
    } finally {
      setSending(false);
    }
  };

  const sizeKb = pdf ? Math.max(1, Math.round(pdf.size / 1024)) : 0;

  return (
    <>
      <Modal
        show={show}
        onHide={sending ? undefined : onClose}
        centered
        size="lg"
        className="send-email-modal"
      >
        <Modal.Header closeButton={!sending}>
          <Modal.Title>Отправить на e-mail</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="send-email__row">
            <span className="send-email__label">Получатель</span>
            <span className="send-email__value">
              {info.loading
                ? "…"
                : info.patient_email || "у пациента не указан e-mail"}
            </span>
          </div>

          {!info.loading && !info.smtp_configured ? (
            <p className="send-email__warn">
              SMTP не настроен. Заполните данные почтового сервера в
              «Настройки → Почта (SMTP)» — без этого отправка недоступна.
            </p>
          ) : null}

          {!info.loading && info.smtp_configured && !info.patient_email ? (
            <p className="send-email__warn">
              У пациента не заполнен e-mail. Добавьте адрес в профиле пациента.
            </p>
          ) : null}

          <div className="send-email__text-block">
            <label className="send-email__label" htmlFor="send-email-subject">
              Тема письма
            </label>
            <input
              id="send-email-subject"
              ref={subjectRef}
              className="form-control send-email__subject"
              value={subjectText}
              onChange={(event) => setSubjectText(event.target.value)}
              placeholder="Тема письма пациенту"
              type="text"
              maxLength={200}
            />

            <label className="send-email__label" htmlFor="send-email-text">
              Сопроводительный текст
            </label>
            <textarea
              id="send-email-text"
              ref={textRef}
              className="form-control send-email__input"
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder="Текст письма пациенту"
              rows={12}
              spellCheck="false"
            />
            <p className="send-email__hint">
              Enter — перенос строки. Отправка выполняется только по кнопке.
            </p>
          </div>

          {linkMode ? (
            <div className="send-email__attachment send-email__attachment--link">
              <span className="send-email__attachment-icon" aria-hidden="true">
                LNK
              </span>
              <span className="send-email__attachment-name">
                {linkUrl
                  ? "Ссылка на анкету — пациент заполнит её онлайн"
                  : linkError || "Создаём ссылку…"}
              </span>
              {linkUrl ? (
                <span
                  className="send-email__attachment-size"
                  title={linkUrl}
                >
                  ссылка
                </span>
              ) : null}
            </div>
          ) : (
            <div className="send-email__attachment">
              <span className="send-email__attachment-icon" aria-hidden="true">
                PDF
              </span>
              <span className="send-email__attachment-name">
                {pdf ? fileName : pdfError || "Формируем PDF…"}
              </span>
              {pdf ? (
                <span className="send-email__attachment-size">{sizeKb} КБ</span>
              ) : null}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={onClose}
            disabled={sending}
            variant="secondary"
          >
            Отмена
          </Button>
          <Button
            icon="send"
            loading={sending}
            onClick={handleSend}
            disabled={!canSend}
            title={
              info.can_send
                ? "Отправить письмо пациенту"
                : "Нужны настроенный SMTP и e-mail пациента"
            }
            variant="primary"
          >
            {sending ? "Отправляем…" : "Отправить"}
          </Button>
        </Modal.Footer>
      </Modal>
      {toast.title ? (
        <Toast key={toast.id} title={toast.title} status={toast.status} />
      ) : null}
    </>
  );
}
